/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { IconEye } from '@tabler/icons-react';

import formatDate from '../../../../utilities/helper';
import useDebounce from '../../../../utilities/useDebounce';
import NewCommonModal from '../../../../components/common/NewCommonModal';
import ListView from '../../../../components/common/ListView';
import TableSkeleton from '../../../../components/skeleton/TableSkeleton';
import Pagination from '../../../../components/common/Pagination';
import NormalSearch from '../../../../components/common/NormalSearch';

import {
  useCreateLocation,
  useLocationList,
  useUpdateLocation,
  useDeleteLocation,
} from './locationHooks';
import BreadCrumbs from './BreadCrumbs';
import CityManageTab from './CityManageTab';
import DistrictManageTab from './DistrictManageTab';
import DynamicForm from './DynamicForm';
import { useLocationSchemas } from './locationSchemas';

interface TabState {
  name: string;
  params: string;
  data: string;
  cityName?: string;
  countryId?: string;
}

interface CountryOption {
  value: string;
  label: string;
  countryCode: string;
  dialCode: string;
  currencyCode: string;
  totalCities?: number;
  totalDistricts?: number;
}

export default function LocationTab() {
  const [searchFor, setSearchFor] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const debouncedSearch = useDebounce(searchFor, 500);
  const { data: countryList, refetch, isPending } = useLocationList({
    page,
    searchFor: debouncedSearch,
  });

  // mutations
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  const deleteLocation = useDeleteLocation();

  /* Modal State */
  const { schemas } = useLocationSchemas();
  const currentSchema = schemas.locationAdd;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const [tab, setTab] = useState<TabState>({
    name: 'loc',
    params: '',
    data: '',
  });
  const [tabHistory, setTabHistory] = useState<TabState[]>([{ name: 'loc', params: '', data: '' }]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<any>({
    mode: 'onSubmit',
    defaultValues: {},
  });

  // watch selected country
  const selectedCountryName = watch('countryName');

  useEffect(() => {
    if (selectedCountryName) {
      const countryOptions = (currentSchema?.fields?.[0]?.options as CountryOption[]) || [];
      const countryDetails = countryOptions.find((opt: any) => opt.value === selectedCountryName);
      if (countryDetails) {
        // Automatically update the dependent fields
        setValue('countryCode', countryDetails.countryCode);
        setValue('dialCode', countryDetails.dialCode);
        setValue('currencyCode', countryDetails.currencyCode);
        setValue('totalCities', countryDetails.totalCities || 0);
        setValue('totalDistricts', countryDetails.totalDistricts || 0);
      }
    }
  }, [selectedCountryName, setValue, currentSchema.fields]);

  // submit
  const handleFormSubmit = (data: Record<string, unknown>) => {
    if (isEdit && selectedItem) {
      updateLocation.mutate(
        { id: selectedItem.id, payload: data },
        {
          onSuccess: () => {
            toast.success('Country updated successfully');
            closeAndReset();
          },
          onError: (error: any) => {
            toast.error(error?.message || 'Update failed');
          },
        },
      );
    } else {
      createLocation.mutate(data, {
        onSuccess: () => {
          toast.success('Country created successfully');
          closeAndReset();
        },
        onError: (error: any) => {
          toast.error(error?.message || 'Creation failed');
        },
      });
    }
  };

  const closeAndReset = () => {
    reset();
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedItem(null);
    refetch();
  };

  useEffect(() => {
    refetch();
  }, []);

  const columns = [
    {
      label: '#',
      render: (_: any, index: number) => (page - 1) * pageSize + (index + 1),
    },
    {
      label: 'Country',
      render: (s: any) => s?.name,
    },
    {
      label: 'Country Code',
      render: (s: any) => s?.countryCode,
    },
    {
      label: 'Currency Code',
      render: (s: any) => s?.currencyCode,
    },
    {
      label: 'Dial Code',
      render: (s: any) => s?.dialCode,
    },
    {
      label: 'Date',
      render: (s: any) => formatDate(s?.date),
    },
    {
      label: 'Action',
      render: (s: any) => (
        <div className="flex gap-2 justify-center">
          <button
            className="px-3 py-1.5 border border-gray-300 hover:border-gray-400 rounded-full bg-[#F7F6F2] hover:bg-gray-200 transition text-sm flex items-center gap-2"
            onClick={() => {
              changeTab({
                name: 'city',
                params: s?.id || '',
                data: s?.name || '',
              });
            }}
          >
            <IconEye size={18} /> View City
          </button>
        </div>
      ),
    },
  ];

  const changeTab = (newTab: TabState) => {
    if (newTab.name !== tab.name) {
      setTab(newTab);
      setTabHistory((prev) => [...prev, newTab]);
    }
  };

  const goBack = () => {
    if (tabHistory.length > 1) {
      const newHistory = [...tabHistory];
      newHistory.pop();
      const previousTab = newHistory[newHistory.length - 1];
      setTab(previousTab);
      setTabHistory(newHistory);
    }
  };

  return (
    <>
      <div className="space-y-6 pt-2">
        <BreadCrumbs tab={tab} goBack={goBack} tabHistory={tabHistory} />
        {tab?.name === 'loc' && (
          <>
            <div className="bg-white rounded-2xl border border-[#d2d2d2] overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h2 className="text-base font-semibold">Country List</h2>
                <div className="flex items-center gap-2">
                  <NormalSearch
                    value={searchFor}
                    onChange={(e: any) => {
                      setSearchFor(e.target.value);
                      setPage(1);
                    }}
                  />
                  {/* <button
                    onClick={() => {
                      setIsEdit(false);
                      setIsModalOpen(true);
                    }}
                    className="px-4 py-2 bg-[#FBD037] hover:bg-yellow-500 text-black font-semibold rounded-full transition-all text-sm flex items-center justify-center"
                  >
                    Add Country
                  </button> */}
                </div>
              </div>

              {isPending ? (
                <TableSkeleton
                  columns={[
                    '#',
                    'Country',
                    'Country Code',
                    'Currency Code',
                    'Dial Code',
                    'Date',
                    'Action',
                  ]}
                  rows={10}
                />
              ) : Array.isArray(countryList?.data?.countries) &&
                countryList.data.countries.length > 0 ? (
                <ListView columns={columns} data={countryList.data.countries} />
              ) : (
                <div className="p-8 text-center text-gray-500">No Data Found</div>
              )}
            </div>

            {Array.isArray(countryList?.data?.countries) &&
              countryList.data.countries.length > 0 && (
                <Pagination
                  totalPages={countryList.data.totalPages}
                  fetchData={(pageNo: number) => setPage(pageNo)}
                  currentPage={page}
                />
              )}
          </>
        )}
      </div>

      <NewCommonModal
        title={isEdit ? 'Update Country' : 'Add New Country'}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        actionButtonText={isEdit ? 'Update' : 'Save'}
        actionButtonOnClick={handleSubmit(handleFormSubmit)}
        closeButtonText="Cancel"
      >
        <div className="bg-white p-6 rounded-b-[30px]">
          <DynamicForm
            schema={currentSchema}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            control={control}
            clearErrors={clearErrors}
          />
        </div>
      </NewCommonModal>

      {tab?.name === 'city' && <CityManageTab tab={tab} setTab={changeTab} />}
      {tab?.name === 'district' && <DistrictManageTab tab={tab} />}
    </>
  );
}
